// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { messages, useMessages, type Language } from '../i18n.js';
import { LanguageSwitch } from './LanguageSwitch.js';
import { useEffect, useState } from 'react';

/**
 * The introduction: one screen saying what Bilal is, then the one screen that says what it is not —
 * shown before the door, once per browser.
 *
 * It was three screens and the disclaimer (founder feedback 2026-09-11, item 3; wording from the
 * redesign canvas): four presses before a person could even see the door. The same three things
 * are said on one screen now (founder, 2026-09-20: "the page should be better overall"; 09-21:
 * getting in should be easy), in the same words and the same order, so what was reviewed is what
 * is still shown.
 *
 * The disclaimer is not skippable and not decorative. It carries the intended-use wording the
 * product is allowed to make — not a therapist, not a mufti; no diagnosis, no treatment, no fatwa —
 * and the emergency line, in the person's own language before they have typed a word.
 */
const T = messages({
  ar: {
    steps: [
      {
        title: 'تحدّث مع بلال',
        body: 'يسمعك بإنصات، ويفكّر معك بهدوء. هو هنا ليسمع أولاً، لا ليقدّم خطبة.',
      },
      {
        title: 'المنهج الإسلامي أولاً',
        body: 'يستند إلى القرآن والسنّة، ويُظهر مصدر كل ما يستشهد به حتى تتحقّق منه بنفسك.',
      },
      {
        title: 'وعلم النفس ثانيًا',
        body: 'يعتمد على مناهج في علم النفس متوائمة مع منهجنا الإسلامي، لتكون التجربة مثرية لا مجرّد كلام.',
      },
    ],
    disclaimerRegion: 'قبل أن نبدأ',
    disclaimerTitle: 'قبل أن نبدأ',
    points: [
      'بلال ليس معالجًا نفسيًا وليس مفتيًا؛ لا يشخّص ولا يعالج ولا يصدر فتوى.',
      'يفكّر معك بهدوء، سؤالاً بعد سؤال.',
      'يستشهد بأدلة دينية ويُظهر مصدر كل اقتباس.',
      'في الحالات الطارئة، تواصل مع مختصّ أو جهة طوارئ فورًا.',
    ],
    understood: 'فهمت، أكمل',
    privacy: 'سياسة الخصوصية',
    terms: 'شروط الاستخدام',
    introRegion: 'التعريف ببلال',
    introTitle: 'تعرّف على بلال',
    start: 'ابدأ الآن',
  },
  en: {
    steps: [
      {
        title: 'Talk to Bilal',
        body: 'He listens closely and thinks with you, calmly. He is here to hear you first, not to preach.',
      },
      {
        title: 'The Islamic frame first',
        body: 'He draws on the Qur’an and the Sunnah, and shows the source of anything he cites so you can check it yourself.',
      },
      {
        title: 'And psychology second',
        body: 'He draws on approaches in psychology that sit with that frame, so the experience is substantial rather than talk.',
      },
    ],
    disclaimerRegion: 'Before we start',
    disclaimerTitle: 'Before we start',
    points: [
      'Bilal is not a therapist and not a mufti; he does not diagnose, treat or issue religious rulings.',
      'He thinks with you calmly, one question at a time.',
      'He cites religious evidence and shows the source of every quotation.',
      'In an emergency, contact a professional or your local emergency services immediately.',
    ],
    understood: 'Understood, continue',
    privacy: 'Privacy policy',
    terms: 'Terms of use',
    introRegion: 'About Bilal',
    introTitle: 'Meet Bilal',
    start: 'Start now',
  },
});

/**
 * One drawn mark per point, in order: being heard, the sources, the mind. Paths that take
 * `currentColor`, never pictograph characters — the platform paints those in its own colours.
 */
const POINT_MARKS: readonly React.JSX.Element[] = [
  <svg key="heard" viewBox="0 0 24 24" width="20" height="20" focusable="false">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 3.5V16h0A2.5 2.5 0 0 1 4 13.5z"
    />
  </svg>,
  <svg key="sources" viewBox="0 0 24 24" width="20" height="20" focusable="false">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.5C10.2 5.2 7.8 4.6 4.5 4.8v13c3.3-.2 5.7.4 7.5 1.7 1.8-1.3 4.2-1.9 7.5-1.7v-13c-3.3-.2-5.7.4-7.5 1.7zm0 0v13"
    />
  </svg>,
  <svg key="mind" viewBox="0 0 24 24" width="20" height="20" focusable="false">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 20s-7-4.4-7-9.6A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7 2.4C19 15.6 12 20 12 20z"
    />
  </svg>,
];

export type OnboardingEvent =
  'onboarding_started' | 'onboarding_step_viewed' | 'onboarding_completed' | 'onboarding_skipped';

export function Onboarding({
  onFinished,
  onEvent,
  language,
  onLanguage,
}: {
  /**
   * The language in force and the way to change it (founder, 2026-09-20). It is here, on the first
   * screen a person ever sees, because the only other switch is in the settings behind a sign-in:
   * someone who cannot read the page cannot be expected to get that far to fix it.
   */
  readonly language: Language;
  readonly onLanguage: (language: Language) => void;
  /** The disclaimer was acknowledged. `skipped` is kept in the type for callers; nothing skips now. */
  readonly onFinished: (how: 'completed' | 'skipped') => void;
  readonly onEvent: (event: OnboardingEvent, properties?: { readonly step: number }) => void;
}): React.JSX.Element {
  const t = useMessages(T);
  const [stage, setStage] = useState<'intro' | 'disclaimer'>('intro');

  useEffect(() => {
    onEvent('onboarding_started');
    onEvent('onboarding_step_viewed', { step: 1 });
    // Once, on mount: the events describe arrival, not re-renders.
  }, []);

  const languageSwitch = <LanguageSwitch language={language} onLanguage={onLanguage} />;

  if (stage === 'disclaimer') {
    return (
      <section aria-label={t.disclaimerRegion} className="onboarding disclaimer">
        <div className="onboarding-card">
          {languageSwitch}
          <h2>{t.disclaimerTitle}</h2>
          <ul className="disclaimer-points">
            {t.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <button
            type="button"
            className="button-primary"
            onClick={() => {
              onFinished('completed');
            }}
          >
            {t.understood}
          </button>
          <p className="legal-links">
            <a href="/privacy">{t.privacy}</a>
            <span aria-hidden="true"> · </span>
            <a href="/terms">{t.terms}</a>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section aria-label={t.introRegion} className="onboarding">
      <div className="onboarding-card">
        {languageSwitch}
        <span className="logo-plate onboarding-logo">
          <img src="/brand/emblem.svg" alt="" className="brand-light" />
          <img src="/brand/emblem-jass.svg" alt="" className="brand-dark" />
        </span>
        <h2>{t.introTitle}</h2>
        <ol className="onboarding-points">
          {t.steps.map((step, index) => (
            <li key={step.title}>
              <span className="onboarding-point-mark" aria-hidden="true">
                {POINT_MARKS[index]}
              </span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <button
          type="button"
          className="button-primary"
          onClick={() => {
            onEvent('onboarding_completed');
            setStage('disclaimer');
          }}
        >
          {t.start}
        </button>
      </div>
    </section>
  );
}
